import {
    AccountRecord,
    ClassifierDetailView,
    ModerationBoundaryView
} from '../../api/client'
import { ModerationBoundaryTemplate, Queue } from '../../models/Moderation'
import Card from '../card/Card'
import SimpleLoader from '../SimpleLoader'
import ModerationBoundaryList from './ModerationBoundaryList'
import NewModerationBoundary from './NewModerationBoundary'

type Props = {
    queue: Queue
    title: string
    /** Tailwind background class for the dot that identifies the queue. */
    dotClassName: string
    account: AccountRecord
    boundaries: ModerationBoundaryView[]
    /** Classifiers that don't yet have a boundary in this queue. */
    availableClassifiers: ClassifierDetailView[]
    loading: boolean
    setSelectedBoundary: (boundary: ModerationBoundaryView) => void
    setEditOpen: (open: boolean) => void
    showCompare: boolean
    compareWith: Map<string, ModerationBoundaryTemplate>
    compareName: string
    onDelete: (boundary: ModerationBoundaryView) => void
    onCreate: () => Promise<void>
    /** Classifier ids whose boundaries are owned by classifier deployment. */
    promptManagedClassifierIds: Set<number>
}

/**
 * One queue's boundaries as a card, so the three queues can sit side by side
 * and read as one family. The coloured dot names the queue; a full-width
 * coloured header bar made the screen shout, and brand red is meant to be the
 * only saturated colour in the chrome. — Claude
 */
export default function ModerationQueueCard(props: Props) {
    const {
        queue,
        title,
        dotClassName,
        account,
        boundaries,
        availableClassifiers,
        loading,
        setSelectedBoundary,
        setEditOpen,
        showCompare,
        compareWith,
        compareName,
        onDelete,
        onCreate,
        promptManagedClassifierIds
    } = props

    return (
        <Card
            compact
            title={title}
            icon={<span className={`block h-2.5 w-2.5 rounded-full ${dotClassName}`} />}
            description={`${boundaries.length} ${boundaries.length === 1 ? 'classifier' : 'classifiers'}`}
            footer={
                <NewModerationBoundary
                    account={account}
                    classifierDetailsList={availableClassifiers}
                    queue={queue}
                    promptManagedClassifierIds={promptManagedClassifierIds}
                    onCreate={onCreate}
                />
            }
        >
            <ModerationBoundaryList
                boundaries={boundaries}
                setSelectedBoundary={setSelectedBoundary}
                setEditOpen={setEditOpen}
                showCompare={showCompare}
                compareWith={compareWith}
                compareName={compareName}
                onDelete={onDelete}
                promptManagedClassifierIds={promptManagedClassifierIds}
            />
            {loading && <SimpleLoader loading={true} />}
        </Card>
    )
}
